/**
 * SuggestService — autocomplete suggestions with debounce and request cancellation
 */

import { ENDPOINTS } from '../api/endpoints.js';
import { EVENTS } from '../core/events.js';
import debounce from '../utils/debounce.js';

export default class SuggestService {
  /**
   * @param {import('../api/ApiClient.js').default} apiClient
   * @param {import('../core/StateManager.js').default} stateManager
   * @param {import('../core/EventBus.js').default} eventBus
   * @param {import('../core/Config.js').default} config
   */
  constructor(apiClient, stateManager, eventBus, config) {
    this._apiClient = apiClient;
    this._stateManager = stateManager;
    this._eventBus = eventBus;
    this._config = config;

    this._currentController = null;

    // Debounced suggest function
    this._debouncedSuggest = debounce((query) => {
      this._fetchSuggestions(query);
    }, this._config.debounceMs);
  }

  /**
   * Request suggestions for query (debounced)
   * @param {string} query
   */
  suggest(query) {
    const trimmed = (query || '').trim();

    if (trimmed.length < this._config.suggestMinChars) {
      this._cancelPrevious();
      this._debouncedSuggest.cancel();
      this._stateManager.setState({ suggestions: [] });
      this._eventBus.emit(EVENTS.SUGGEST_RESULTS, { suggestions: [] });
      return;
    }

    this._debouncedSuggest(trimmed);
  }

  /**
   * Clear suggestions
   */
  clear() {
    this._debouncedSuggest.cancel();
    this._cancelPrevious();
    this._stateManager.setState({ suggestions: [] });
    this._eventBus.emit(EVENTS.SUGGEST_RESULTS, { suggestions: [] });
  }

  /** @private */
  async _fetchSuggestions(query) {
    this._cancelPrevious();

    const controller = this._apiClient.createAbortController();
    this._currentController = controller;

    try {
      const result = await this._apiClient.get(ENDPOINTS.SUGGEST, { q: query }, {
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      const suggestions = (result.data && result.data.suggestions) || [];

      this._stateManager.setState({ suggestions });
      this._eventBus.emit(EVENTS.SUGGEST_RESULTS, { suggestions });
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.warn('[GarderoboSearch] Suggest error:', err);
      // Silently fail — don't show error for suggestions
    } finally {
      if (this._currentController === controller) {
        this._currentController = null;
      }
    }
  }

  /** @private */
  _cancelPrevious() {
    if (this._currentController) {
      this._currentController.abort();
      this._currentController = null;
    }
  }

  destroy() {
    this._debouncedSuggest.cancel();
    this._cancelPrevious();
  }
}
